import {
  confirmSignUp,
  resendSignUpCode,
  configureAuth,
  getCurrentPoolId,
  signOut,
} from '@lastbrand/fe-common/universal-auth'
import { useTranslation } from 'next-i18next'
import { useCallback, useRef, useState } from 'react'

import { FALLBACK_REGION_ID } from '@src/config/localisation/locale-config.constants'
import { fetchIdentityConfigByEmail, mapIdentityConfigToAuthConfig } from '@src/modules/auth/identity-config-api'
import { getUserAgnosticGlobalFeatureGatesFromMemory } from '@src/modules/global-state/user-agnostic-feature-gates.browser'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import SignupOTPModal from '@src/pages/sign-up/sign-up-otp-modal/SignupOTPModal'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent, trackInternalEvent } from '@src/telemetry/track'
import { getLocalisationDataFromStorage } from '@src/utils/general.utils'

import useLoginUser from '../../../../hooks/useLoginUser'
import { getSanitizedRedirectUrls } from '../../../Auth/utils'

import { InitialPage } from './Views/InitialPage'
import { SecondaryPage } from './Views/SecondaryPage'

interface LoginVariantAPropTypes {
  serverError?: { message: string }
  unverifiedAccount?: boolean
  resendVerificationEmail: (email?: string) => void
  verificationEmailSent?: boolean
  email: string
  register: (x: string, y: object) => object
  errors: Record<string, { message: string }>
  backToLogin: () => void
  loading?: boolean
  page: any
  pageState: 'INITIAL' | 'SECONDARY'
  login: (data: object) => void
  openResetPasswordModal: (email: string) => void
  isInResetPasswordFlow?: boolean
}

const LoginVariantA = (props: LoginVariantAPropTypes) => {
  const router = useRouter()
  const email = useRef<string | undefined>()
  const [pageState, setPageState] = useState('INITIAL')
  const [pageLoading, setPageLoading] = useState(false)
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false)
  const [otpError, setOtpError] = useState<string | null>(null)
  const [pageError, setPageError] = useState<string | null>(null)
  const [resendLoading, setResendLoading] = useState(false)
  const [verifyLoading, setVerifyLoading] = useState(false)
  const { t } = useTranslation('accountPage')
  // TODO-SSR: Add useLoginUser function back after fixing the provider for auth
  const { loginUser } = useLoginUser()

  const onPageStateChange = (username: string) => {
    email.current = username
    setPageLoading(true)

    const locData = getLocalisationDataFromStorage()
    const regionId = locData?.regionId ?? FALLBACK_REGION_ID

    if (locData?.regionId == null) {
      // @i18n-ignore
      captureException(new Error(`Fallback to FALLBACK_REGION_ID for login`), {
        module: 'UNKNOWN',
        feature: 'UNKNOWN',
        extraInfo: {
          // eslint-disable-next-line
          identifier: 'LoginVariantA_onPageStateChange',
          locale: locData?.locale,
          country: locData?.country,
        },
      })
      // @i18n-ignore
      trackInternalEvent('FALLBACK_REGION_ID', {
        // eslint-disable-next-line
        identifier: 'LoginVariantA_onPageStateChange',
        locale: locData?.locale,
        country: locData?.country,
      })
    }

    const proceedWithCognitoSignIn = () => {
      loginUser(username, '#')
        .then(response => {
          trackEvent(TELEMETRY_EVENT_NAMES.API_RESPONSE, {
            apiName: `login`,
            status: `success`,
            context: {
              login_email: username,
              isSignedIn: Boolean(response?.isSignedIn),
              needSignUpConfirmation: Boolean(response?.needSignUpConfirmation),
              resetPasswordRequired: Boolean(response?.resetPasswordRequired),
            },
          })

          if (response.needSignUpConfirmation) {
            setIsOtpModalOpen(true)
            setPageLoading(false)
            return
          }

          if (response.resetPasswordRequired) {
            props.openResetPasswordModal(username)
            setPageLoading(false)
            return
          }

          if (!response.isSignedIn) {
            setPageState('SECONDARY')
          } else {
            setPageError(response.codeMedium)
          }
          setPageLoading(false)
        })
        .catch((error: unknown) => {
          const errorCode = error && (error as { name: string; message: string }).name
          const errorMessage = error && (error as { message: string }).message

          trackEvent(TELEMETRY_EVENT_NAMES.API_RESPONSE, {
            apiName: `login`,
            status: `failure`,
            context: {
              email: username,
              error: errorMessage,
              errorType: errorCode,
            },
          })

          if (errorCode === 'UserNotConfirmedException') {
            setIsOtpModalOpen(true)
            setPageLoading(false)
            void resendOtp()
          }
          if (errorCode === 'PasswordResetRequiredException') {
            props.openResetPasswordModal(username)
          }

          if (error && (error as { name: string }).name === 'LimitExceededException') {
            setIsOtpModalOpen(false)
            setPageError(t('verification_attempts_limit'))
          }
          if (errorCode === 'UserNotFoundException') {
            const redirectUri = getSanitizedRedirectUrls()
            const navigateTo =
              redirectUri && redirectUri !== ''
                ? `/sign-up?redirecturi=${redirectUri}&userMail=${btoa(username)}`
                : `/sign-up/?userMail=${btoa(username)}`

            router.replace(navigateTo)
          } else if (error && (error as { name: string }).name === 'NotAuthorizedException') {
            setPageState('SECONDARY')
          }
          setPageLoading(false)
        })
    }

    if (getUserAgnosticGlobalFeatureGatesFromMemory().multi_pool_auth_enabled) {
      fetchIdentityConfigByEmail(regionId, username)
        .then(apiResponse => {
          const currentPoolId = getCurrentPoolId()
          const targetPoolId = apiResponse.cognitoPoolId
          if (currentPoolId != null && targetPoolId === currentPoolId) {
            // Pool already matches API: no signOut/configureAuth — still run the same Cognito step as other paths.
            proceedWithCognitoSignIn()
            return
          }

          void signOut()
            .then(() => {
              const authConfig = mapIdentityConfigToAuthConfig(apiResponse)
              return configureAuth({
                ...authConfig,
                onError(err: Error, context: Record<string, unknown>) {
                  captureException(err, {
                    module: MODULES.UNIVERSAL_AUTH,
                    feature: FEATURES.UNIVERSAL_AUTH,
                    extraInfo: context,
                  })
                },
              })
            })
            .then(() => {
              proceedWithCognitoSignIn()
            })
            .catch((err: unknown) => {
              captureException(err as Error, {
                module: MODULES.UNIVERSAL_AUTH,
                feature: FEATURES.UNIVERSAL_AUTH,
                extraInfo: { username, regionId, type: 'API_FAILURE', API: 'AUTH_REINIT' },
              })
              setPageLoading(false)
            })
        })
        .catch(() => {
          setPageLoading(false)
          proceedWithCognitoSignIn()
        })
    } else {
      proceedWithCognitoSignIn()
    }
  }

  const onEmailEdit = useCallback(() => {
    setPageState('INITIAL')
  }, [])

  const verifyOtp = async (otp: string) => {
    try {
      setVerifyLoading(true)
      setOtpError(null)
      const confirmSignUpResponse = await confirmSignUp(email.current ?? '', otp)

      if (confirmSignUpResponse.isSignUpComplete) {
        setIsOtpModalOpen(false)
        // Redirect to login page or automatically log the user in
        setPageState('SECONDARY')
      }
    } catch (err) {
      if (err && (err as any).name === 'CodeMismatchException') {
        setOtpError(err.message || t('incorrect_verification_code'))
      } else if (err && (err as any).name === 'ExpiredCodeException') {
        setOtpError(err.message || t('verification_code_expired'))
      } else {
        setOtpError(err.message || t('account_verification_failed'))
      }
    } finally {
      setVerifyLoading(false)
    }
  }

  const resendOtp = async () => {
    try {
      const shouldIncludeLocale =
        typeof window !== 'undefined' && window.__debug?.isI18nEnabled && getLocalisationDataFromStorage()?.locale
      setResendLoading(true)
      await resendSignUpCode(email.current ?? '', {
        ...(shouldIncludeLocale
          ? {
              locale: getLocalisationDataFromStorage()?.locale?.toString(),
            }
          : {}),
      })
      setResendLoading(false)
    } catch (err) {
      if (err && (err as any).name === 'LimitExceededException') {
        setIsOtpModalOpen(false)
        setPageError((err as Error).message || t('verification_attempts_limit'))
      } else {
        setOtpError(err.message || t('verification_resend_failed'))
      }
      setResendLoading(false)
    }
  }

  return (
    <>
      {!props.isInResetPasswordFlow && pageState === 'INITIAL' ? (
        <InitialPage
          {...props}
          onPageStateChange={onPageStateChange}
          loading={pageLoading || props.loading}
          email={email.current}
          error={pageError}
        />
      ) : (
        <SecondaryPage
          {...props}
          email={email.current}
          loading={pageLoading || props.loading}
          onEmailEdit={onEmailEdit}
          isInResetPasswordFlow={props.isInResetPasswordFlow}
          error={pageError}
        />
      )}

      <SignupOTPModal
        open={isOtpModalOpen}
        onClose={() => {
          setIsOtpModalOpen(false)
          setOtpError(null)
        }}
        email={email.current ?? ''}
        onSubmit={otp =>
          void verifyOtp(otp).catch((err: unknown) => {
            console.error('OTP verification error:', err)
          })
        }
        onResend={() =>
          void resendOtp().catch((err: unknown) => {
            console.error('OTP resend error:', err)
          })
        }
        error={otpError}
        loading={verifyLoading}
        resendLoading={resendLoading}
      />
    </>
  )
}

export { LoginVariantA }
